/**
 * Parameter Type Templates Configuration
 * 
 * Provides smart defaults and templates for different parameter types
 * when cloning APIs for multiple parameter types.
 */

export interface ParameterTypeTemplate {
  queryFormat: string;
  suggestedPrice: number;
  icon: string;
  exampleValue: string;
  keepParams: string[];  // Parameters to keep from source API
  countryCodeFormat?: 'none' | 'numeric' | 'plus_prefix' | 'iso_code';
  description: string;
}

export const PARAMETER_TYPE_TEMPLATES: Record<string, ParameterTypeTemplate> = {
  phone_number: {
    queryFormat: '+{country_code}{value}',
    suggestedPrice: 5,
    icon: '📱',
    exampleValue: '+919876543210',
    keepParams: ['country', 'lang', 'countryCode', 'code'],
    countryCodeFormat: 'numeric',
    description: 'Phone number with country code'
  },
  
  email: {
    queryFormat: '{value}',
    suggestedPrice: 3,
    icon: '📧',
    exampleValue: 'user@example.com',
    keepParams: ['lang'],
    countryCodeFormat: 'none',
    description: 'Email address search'
  },
  
  username: {
    queryFormat: '@{value}',
    suggestedPrice: 4,
    icon: '👤',
    exampleValue: '@johndoe',
    keepParams: ['lang', 'country'],
    countryCodeFormat: 'none',
    description: 'Username or handle search'
  },
  
  domain: {
    queryFormat: 'site:{value}',
    suggestedPrice: 3,
    icon: '🌐',
    exampleValue: 'site:example.com',
    keepParams: ['lang'],
    countryCodeFormat: 'none',
    description: 'Domain or website search'
  },
  
  ip_address: {
    queryFormat: '{value}',
    suggestedPrice: 3,
    icon: '🔢',
    exampleValue: '8.8.8.8',
    keepParams: [],
    countryCodeFormat: 'none',
    description: 'IP address lookup'
  },
  
  social_media_handle: {
    queryFormat: '@{value}',
    suggestedPrice: 4,
    icon: '📱',
    exampleValue: '@elonmusk',
    keepParams: ['country', 'lang'],
    countryCodeFormat: 'none',
    description: 'Social media handle search'
  },
  
  name: {
    queryFormat: '{value}',
    suggestedPrice: 4,
    icon: '👥',
    exampleValue: 'John Doe',
    keepParams: ['country', 'lang'],
    countryCodeFormat: 'none',
    description: 'Person name search'
  },
  
  company_name: {
    queryFormat: '{value}',
    suggestedPrice: 4,
    icon: '🏢',
    exampleValue: 'Google Inc',
    keepParams: ['country', 'lang'],
    countryCodeFormat: 'none',
    description: 'Company or business name'
  },
  
  vehicle_number: {
    queryFormat: '{value}',
    suggestedPrice: 5,
    icon: '🚗',
    exampleValue: 'ABC123',
    keepParams: ['country', 'state'],
    countryCodeFormat: 'none',
    description: 'Vehicle registration number'
  },
  
  aadhaar_number: {
    queryFormat: '{value}',
    suggestedPrice: 6,
    icon: '🆔',
    exampleValue: '1234-5678-9012',
    keepParams: ['country'],
    countryCodeFormat: 'none',
    description: 'Aadhaar or ID number'
  }
};

/**
 * Get template for a parameter type by name
 */
export function getParameterTypeTemplate(typeName: string): ParameterTypeTemplate | null {
  return PARAMETER_TYPE_TEMPLATES[typeName] || null;
}

/**
 * Generate API name based on source and new parameter type
 */
export function generateClonedApiName(sourceApiName: string, newTypeName: string): string {
  // Remove existing type suffix if present
  const baseName = sourceApiName
    .replace(/\s*-\s*(Phone Number|Email|Username|Domain|IP Address|Social Media Handle|Name|Company Name|Vehicle Number|Aadhaar Number|General Search).*$/i, '')
    .trim();
  
  // Format type name nicely
  const formattedTypeName = newTypeName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  
  return `${baseName} - ${formattedTypeName}`;
}

/**
 * Adjust request parameters based on parameter type template
 */
export function adjustParametersForType(
  sourceParams: Record<string, any>,
  template: ParameterTypeTemplate
): Record<string, any> {
  const newParams: Record<string, any> = {};
  
  // Keep only parameters specified in template
  for (const [key, value] of Object.entries(sourceParams)) {
    const keyLower = key.toLowerCase();
    
    // Always keep if it's in the keepParams list
    if (template.keepParams.some(param => keyLower.includes(param.toLowerCase()))) {
      newParams[key] = value;
    }
    
    // If it's the main search parameter (contains 'query', 'value', 'search', etc.)
    if (keyLower === 'query' || keyLower === 'search' || keyLower === 'value' || 
        value === '{value}' || value === '{phone_number}') {
      newParams[key] = template.queryFormat;
    }
  }
  
  // If no query parameter was found, add one
  if (!Object.keys(newParams).some(key => {
    const val = newParams[key];
    return typeof val === 'string' && (val.includes('{value}') || val === template.queryFormat);
  })) {
    newParams.query = template.queryFormat;
  }
  
  return newParams;
}

/**
 * Get available parameter types that can be cloned to
 * (exclude types that already exist for the same base API)
 */
export function getAvailableParameterTypes(
  allParameterTypes: any[],
  sourceApi: any,
  existingApis: any[]
): any[] {
  // Extract base name from source API
  const baseName = sourceApi.name
    .replace(/\s*-\s*(Phone Number|Email|Username|Domain|IP Address|Social Media Handle|Name|Company Name|Vehicle Number|Aadhaar Number|General Search).*$/i, '')
    .trim();
  
  // Find existing APIs with the same base name
  const existingTypes = existingApis
    .filter(api => {
      const apiBaseName = api.name
        .replace(/\s*-\s*(Phone Number|Email|Username|Domain|IP Address|Social Media Handle|Name|Company Name|Vehicle Number|Aadhaar Number|General Search).*$/i, '')
        .trim();
      return apiBaseName === baseName;
    })
    .map(api => api.searchParameterTypeId || api.search_parameter_type_id);
  
  // Return only parameter types that don't already exist
  return allParameterTypes.filter(type => !existingTypes.includes(type.id));
}

